import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import styles from './Modal.module.css'

interface ModalProps {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg'
}

export function Modal({ title, onClose, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // Per React Portal direkt in document.body gerendert, nicht inline im Komponentenbaum:
  // ein verschachteltes Modal (z.B. Dokument-Vorschau-Popup, das aus dem Aufgaben-Detail-
  // Modal heraus geöffnet wird) würde sonst durch das äußere .overlay's backdrop-filter
  // einen neuen CSS-Containing-Block für seine eigene position:fixed-Positionierung
  // bekommen — es würde dann relativ zum (scrollbaren, ggf. hochgescrollten) äußeren
  // Overlay statt zum echten Viewport positioniert, oft weit außerhalb des sichtbaren
  // Bereichs. Ein Portal umgeht das vollständig.
  return createPortal(
    <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={[styles.panel, size === 'lg' ? styles.panelLg : ''].join(' ')} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
